import { exerciseKeys } from "../exercise/keys";
import { changeKeys, pendingKeys, planKeys } from "../plan/keys";
import { progressKeys } from "../progress/keys";
import { composeQuickWorkout, correctSet, endSession, fetchExerciseHistory, fetchExerciseRecords, fetchSession, fetchSessionSets, fetchSessions, fetchTrainingStats, getSessionSummary, recordSet, removeSet, startSession, submitSessionFeedback, submitSoreness, } from "./api";
import { workoutKeys } from "./keys";
/**
 * The workout domain as option objects (`../core/options.ts`). The app spreads
 * them into its hooks (T11); nothing here knows about React or TanStack.
 *
 * ── WHAT EACH WRITE INVALIDATES ─────────────────────────────────────────────
 * - A SET write (record, correct, remove) changes the exercise history, the
 *   session it belongs to (and so its summary), the history LIST — every
 *   `workoutSessionView` there carries its sets — and the account's change
 *   feed, which lists sets.
 * - A START adds a row to the list and to the change feed.
 * - An END changes the list and the session, the stats (an ended session is
 *   what the streak and the milestones count), the change feed, and the plan
 *   in effect: the end advances the plan, gated on `changed`
 *   (`apps/api/src/routes/workout.ts`, `advancePlan`).
 * - FEEDBACK and SORENESS change no read this package serves, so they
 *   invalidate nothing.
 *
 * Invalidation is by PREFIX, and a def is built once per client, not per
 * input, so a session write names every session (`sessionAll`) rather than
 * the one it touched. The cost is refetching the detail queries that are
 * mounted — one, in practice.
 */
/** The server's own default, written into the key so an omitted locale and an
 *  explicit `"en"` are ONE cache entry rather than two (`planKeys` states the
 *  rule for the plan's reads). */
const DEFAULT_SET_LIST_LOCALE = "en";
const SET_WRITES = [
    workoutKeys.historyAll,
    // Folded from the same sets as the history (`D199`), under its own prefix
    // for the reason `workoutKeys.records` gives.
    workoutKeys.recordsAll,
    // Goals and strength levels are folded from the same sets (`D200`, `D208`).
    exerciseKeys.goalsAll,
    exerciseKeys.strengthAll,
    workoutKeys.sessionAll,
    workoutKeys.sessionsAll,
    changeKeys.all,
    progressKeys.all,
];
export function sessionsOptions(api, range = {}) {
    return {
        queryFn: ({ signal }) => fetchSessions(api, range, { signal }),
        queryKey: workoutKeys.sessions(range),
    };
}
export function sessionOptions(api, sessionId) {
    return {
        queryFn: ({ signal }) => fetchSession(api, sessionId, { signal }),
        queryKey: workoutKeys.session(sessionId),
    };
}
/**
 * The summary of an ended session. It does not change once the session has
 * ended, so it never goes stale on its own; a set written into the session
 * afterwards invalidates it through `sessionAll`. An open session is a 409
 * `workout.session_not_ended`, which TanStack keeps as an error, not as data.
 */
export function sessionSummaryOptions(api, sessionId) {
    return {
        queryFn: ({ signal }) => getSessionSummary(api, sessionId, { signal }),
        queryKey: workoutKeys.summary(sessionId),
        staleTime: Number.POSITIVE_INFINITY,
    };
}
/**
 * A past session's sets, for correcting one from the history (`D11`).
 *
 * ── THE KEY SITS UNDER THE SESSION'S, LIKE THE SUMMARY'S ───────────────────
 * `["workout","session",id,"sets",locale]`, so `sessionAll` — which every set
 * write invalidates (`SET_WRITES`) — reaches it. That is the whole reason a
 * correction made from this list also moves the summary's totals on the same
 * screen: both hang off one prefix and both refetch.
 *
 * The LOCALE is part of the key, exactly as it is in `planKeys` and for the
 * same reason: the names come back resolved, so an English and a Portuguese
 * read are two answers, not one that flips language.
 *
 * No `staleTime`: unlike the summary, this list DOES change after the session
 * ended — correcting a set is what it is for.
 */
export function sessionSetsOptions(api, sessionId, options = {}) {
    return {
        queryFn: ({ signal }) => fetchSessionSets(api, sessionId, options, { signal }),
        queryKey: [
            ...workoutKeys.session(sessionId),
            "sets",
            options.locale ?? DEFAULT_SET_LIST_LOCALE,
        ],
    };
}
export function exerciseHistoryOptions(api, range = {}) {
    return {
        queryFn: ({ signal }) => fetchExerciseHistory(api, range, { signal }),
        queryKey: workoutKeys.history(range),
    };
}
/**
 * One exercise's records and series (`D199`). Every set write refetches it
 * (`SET_WRITES` names `recordsAll`), so a corrected set moves the record the
 * sheet shows.
 */
export function exerciseRecordsOptions(api, exerciseId) {
    return {
        queryFn: ({ signal }) => fetchExerciseRecords(api, exerciseId, { signal }),
        queryKey: workoutKeys.records(exerciseId),
    };
}
export function trainingStatsOptions(api) {
    return {
        queryFn: ({ signal }) => fetchTrainingStats(api, { signal }),
        queryKey: workoutKeys.stats,
    };
}
/**
 * "Treino rápido" (`D249`): compose today's session. Nothing to invalidate —
 * the server wrote nothing; the start that follows is the outbox's.
 */
export function quickWorkoutMutation(api, options = {}) {
    return {
        invalidates: [],
        mutationFn: (input) => composeQuickWorkout(api, input, options),
        mutationKey: ["workout", "quick", options.locale ?? "en"],
    };
}
export function startSessionMutation(api) {
    return {
        invalidates: [workoutKeys.sessionsAll, changeKeys.all],
        mutationFn: (input) => startSession(api, input),
        mutationKey: ["workout", "sessions", "start"],
    };
}
/** `changed: false` resolves, it does not throw — see {@link endSession}. */
export function endSessionMutation(api) {
    return {
        invalidates: [
            workoutKeys.sessionsAll,
            workoutKeys.sessionAll,
            workoutKeys.stats,
            planKeys.activeAll,
            changeKeys.all,
            progressKeys.all,
            pendingKeys.all,
        ],
        mutationFn: ({ sessionId, ...input }) => endSession(api, sessionId, input),
        mutationKey: ["workout", "sessions", "end"],
    };
}
export function recordSetMutation(api) {
    return {
        invalidates: SET_WRITES,
        mutationFn: ({ sessionId, ...input }) => recordSet(api, sessionId, input),
        mutationKey: ["workout", "sets", "record"],
    };
}
export function correctSetMutation(api) {
    return {
        invalidates: SET_WRITES,
        mutationFn: ({ setId, ...input }) => correctSet(api, setId, input),
        mutationKey: ["workout", "sets", "correct"],
    };
}
export function removeSetMutation(api) {
    return {
        invalidates: SET_WRITES,
        mutationFn: ({ setId }) => removeSet(api, setId),
        mutationKey: ["workout", "sets", "remove"],
    };
}
export function sessionFeedbackMutation(api) {
    return {
        invalidates: [],
        mutationFn: ({ sessionId, ...input }) => submitSessionFeedback(api, sessionId, input),
        mutationKey: ["workout", "sessions", "feedback"],
    };
}
export function sorenessMutation(api) {
    return {
        // The check-in card, the nav badge and the pain insight read these.
        invalidates: [pendingKeys.all, progressKeys.all],
        mutationFn: ({ sessionId, ...input }) => submitSoreness(api, sessionId, input),
        mutationKey: ["workout", "sessions", "soreness"],
    };
}
//# sourceMappingURL=options.js.map